import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const FILTER_MODES = Object.freeze(['low-pass', 'high-pass', 'band-pass', 'notch', 'all-pass'] as const);
export type FilterMode = (typeof FILTER_MODES)[number];

export interface StateVariableFilterOptions {
  readonly sampleRate: number;
  readonly mode?: FilterMode;
  readonly cutoffHz?: number;
  readonly q?: number;
  readonly keyTrack?: number;
  readonly smoothingSeconds?: number;
}

const DENORMAL = 1e-30;
const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};
const flush = (value: number): number => Math.abs(value) < DENORMAL ? 0 : value;

export class StateVariableFilter {
  readonly sampleRate: number;
  private mode: FilterMode;
  private cutoffHz: number;
  private q: number;
  private keyTrack: number;
  private smoothingSeconds: number;
  private currentCutoff: number;
  private currentQ: number;
  private cutoffStep = 0;
  private qStep = 0;
  private cutoffRamp = 0;
  private qRamp = 0;
  private ic1eq = 0;
  private ic2eq = 0;

  constructor(options: StateVariableFilterOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.mode = options.mode ?? 'low-pass';
    if (!(FILTER_MODES as readonly string[]).includes(this.mode)) throw new RangeError('mode is invalid');
    this.cutoffHz = options.cutoffHz ?? 1_000;
    this.q = options.q ?? Math.SQRT1_2;
    this.keyTrack = options.keyTrack ?? 0;
    this.smoothingSeconds = options.smoothingSeconds ?? 0.01;
    this.validate();
    this.currentCutoff = this.cutoffHz;
    this.currentQ = this.q;
  }

  reset(): void {
    this.ic1eq = 0; this.ic2eq = 0;
    this.currentCutoff = this.cutoffHz; this.currentQ = this.q;
    this.cutoffStep = 0; this.qStep = 0; this.cutoffRamp = 0; this.qRamp = 0;
  }

  setParameter(id: string, value: number): void {
    if (id === 'mode') {
      if (!Number.isInteger(value) || value < 0 || value >= FILTER_MODES.length) throw new RangeError('mode must be an integer in [0, 4]');
      this.mode = FILTER_MODES[value]!; return;
    }
    if (id === 'cutoff-hz') { assertRange(id, value, 10, this.maximumCutoff()); this.cutoffHz = value; this.scheduleCutoff(); return; }
    if (id === 'q') { assertRange(id, value, 0.25, 20); this.q = value; this.scheduleQ(); return; }
    if (id === 'key-track') { assertRange(id, value, 0, 1); this.keyTrack = value; return; }
    if (id === 'smoothing-seconds') { assertRange(id, value, 0, 1); this.smoothingSeconds = value; return; }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(input: Float32Array, output: Float32Array, note?: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames || (note && note.length < frames)) {
      throw new RangeError('buffers are too short for frames');
    }
    for (let index = 0; index < frames; index += 1) {
      if (!Number.isFinite(input[index])) throw new RangeError('input samples must be finite');
      if (note && !Number.isFinite(note[index])) throw new RangeError('note samples must be finite');
    }
    for (let index = 0; index < frames; index += 1) {
      const sample = input[index]!;
      this.advanceRamps();
      const midiNote = note?.[index] ?? 69;
      const tracked = this.currentCutoff * 2 ** (((midiNote - 69) * this.keyTrack) / 12);
      const frequency = Math.max(10, Math.min(this.maximumCutoff(), tracked));
      const g = Math.tan(Math.PI * frequency / this.sampleRate);
      const k = 1 / this.currentQ;
      const a1 = 1 / (1 + g * (g + k));
      const a2 = g * a1;
      const a3 = g * a2;
      const v3 = sample - this.ic2eq;
      const band = a1 * this.ic1eq + a2 * v3;
      const low = this.ic2eq + a2 * this.ic1eq + a3 * v3;
      const high = sample - k * band - low;
      this.ic1eq = flush(2 * band - this.ic1eq);
      this.ic2eq = flush(2 * low - this.ic2eq);
      if (!Number.isFinite(this.ic1eq) || !Number.isFinite(this.ic2eq)) {
        this.ic1eq = 0; this.ic2eq = 0; output[index] = 0; continue;
      }
      const notch = high + low;
      const allPass = notch - k * band;
      const value = this.mode === 'low-pass' ? low
        : this.mode === 'high-pass' ? high
        : this.mode === 'band-pass' ? band
        : this.mode === 'notch' ? notch : allPass;
      output[index] = flush(Number.isFinite(value) ? value : 0);
    }
  }

  private maximumCutoff(): number { return Math.max(10, this.sampleRate * 0.49); }
  private rampSamples(): number { return Math.max(0, Math.round(this.smoothingSeconds * this.sampleRate)); }
  private scheduleCutoff(): void {
    const count = this.rampSamples();
    if (count === 0) { this.currentCutoff = this.cutoffHz; this.cutoffRamp = 0; this.cutoffStep = 0; return; }
    this.cutoffStep = (this.cutoffHz - this.currentCutoff) / count; this.cutoffRamp = count;
  }
  private scheduleQ(): void {
    const count = this.rampSamples();
    if (count === 0) { this.currentQ = this.q; this.qRamp = 0; this.qStep = 0; return; }
    this.qStep = (this.q - this.currentQ) / count; this.qRamp = count;
  }
  private advanceRamps(): void {
    if (this.cutoffRamp > 0) { this.currentCutoff += this.cutoffStep; if (--this.cutoffRamp === 0) this.currentCutoff = this.cutoffHz; }
    if (this.qRamp > 0) { this.currentQ += this.qStep; if (--this.qRamp === 0) this.currentQ = this.q; }
  }
  private validate(): void {
    assertRange('cutoffHz', this.cutoffHz, 10, this.maximumCutoff());
    assertRange('q', this.q, 0.25, 20);
    assertRange('keyTrack', this.keyTrack, 0, 1);
    assertRange('smoothingSeconds', this.smoothingSeconds, 0, 1);
  }
}

class FilterModuleInstance implements DspModuleInstance {
  private readonly filter: StateVariableFilter;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.filter = new StateVariableFilter({ sampleRate }); }
  reset(): void { this.filter.reset(); }
  setParameter(id: string, value: number): void { this.filter.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const input = block.inputs.in; const output = block.outputs.out; const note = block.controls.note;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    if (note && note.length < block.frames) throw new RangeError('note buffer is too short');
    this.filter.processInto(input, output, note, block.frames);
  }
}

export const mod019 = defineDspModule({
  descriptor: {
    id: 'mir.mod-019', name: 'Multimode state-variable filter', version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }, { id: 'note', kind: 'control', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'mode', defaultValue: 0, minValue: 0, maxValue: 4, unit: 'enum' },
      { id: 'cutoff-hz', defaultValue: 1_000, minValue: 10, maxValue: 24_000, unit: 'Hz' },
      { id: 'q', defaultValue: Math.SQRT1_2, minValue: 0.25, maxValue: 20, unit: 'ratio' },
      { id: 'key-track', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'smoothing-seconds', defaultValue: 0.01, minValue: 0, maxValue: 1, unit: 's' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new FilterModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
